import { formatChips } from '@polf/shared/locale'
import { useEffect, useRef, useState } from 'react'

import BottomSheet from '../../components/BottomSheet'

/**
 * The pro's "place a bet for this player" sheet. Preset amounts, a custom
 * amount, or all-in. Built on the shared BottomSheet + the score sheets'
 * frame; the preset grid copies ScoreButtons' cell grammar but uses the
 * primary-green selection (a bet is an action, not a score term).
 */

const PRESETS = [200, 300, 400, 500, 1000, 2500, 5000, 7500, 10000]

type Selection = number | 'other' | 'allin' | null

const PlaceBetSheet = ({
  open,
  playerName,
  saving,
  onClose,
  onSave,
}: {
  open: boolean
  playerName: string
  saving: boolean
  onClose: () => void
  onSave: (arg: { bet: number; allIn: boolean }) => void
}) => {
  const [selected, setSelected] = useState<Selection>(null)
  const [custom, setCustom] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (open) {
      setSelected(null)
      setCustom('')
    }
  }, [open])
  useEffect(() => {
    if (selected !== 'other') return undefined
    const id = window.requestAnimationFrame(() => inputRef.current?.focus())
    return () => window.cancelAnimationFrame(id)
  }, [selected])

  const customParsed = parseInt(custom, 10)
  const customValid = !Number.isNaN(customParsed) && customParsed > 0
  const isAllIn = selected === 'allin'
  const betAmount =
    selected === 'other'
      ? customValid
        ? customParsed
        : null
      : typeof selected === 'number'
      ? selected
      : null
  const valid = isAllIn || betAmount != null

  const cell = (active: boolean, tone: 'green' | 'flag') =>
    `flex items-center justify-center py-3 rounded font-mono num text-base font-bold transition-colors ${
      active
        ? tone === 'green'
          ? 'bg-green-700 text-paper ring-2 ring-green-800'
          : 'bg-flag text-paper ring-2 ring-flag-dark'
        : 'bg-cream-card text-ink-700'
    }`

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel="Sett bet"
      backdropClassName="bg-ink-900/45 backdrop-blur-sm"
      panelClassName="max-w-xl bg-paper"
      showGrabHandle
    >
      <div
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
      >
        <div className="px-6 pt-3 pb-3">
          <h2 className="font-display font-extrabold text-lg text-green-900">
            Sett bet
          </h2>
          <p className="text-sm text-ink-700 mt-0.5 truncate">{playerName}</p>
        </div>

        <div className="px-6">
          <div className="grid grid-cols-3 gap-1.5">
            {PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setSelected(v)}
                className={cell(selected === v, 'green')}
              >
                {formatChips(v)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-1.5 mt-1.5">
            <button
              type="button"
              onClick={() => setSelected('other')}
              className={cell(selected === 'other', 'green')}
            >
              Annet beløp
            </button>
            <button
              type="button"
              onClick={() => setSelected('allin')}
              className={`${cell(isAllIn, 'flag')} uppercase tracking-wide`}
            >
              All-in
            </button>
          </div>

          {selected === 'other' && (
            <input
              ref={inputRef}
              type="number"
              inputMode="numeric"
              min={1}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Beløp i chips"
              className="mt-1.5 w-full px-4 py-3 bg-cream-card rounded-lg text-lg font-mono num focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          )}

          {isAllIn && (
            <p className="mt-2 bg-warn/12 text-warn rounded-lg px-3 py-2 text-xs leading-snug">
              All-in betyr enten at spilleren mister alt, eller at chipsene
              dobles etter at turneringen er avsluttet.
            </p>
          )}
        </div>

        <div className="px-6 mt-4 flex flex-col gap-2">
          <button
            type="button"
            disabled={!valid || saving}
            onClick={() => {
              if (isAllIn) onSave({ bet: 0, allIn: true })
              else if (betAmount != null)
                onSave({ bet: betAmount, allIn: false })
            }}
            className="w-full py-3 rounded-xl font-display font-extrabold text-base bg-green-700 hover:bg-green-800 text-paper transition-colors disabled:opacity-50"
            style={{ boxShadow: '0 8px 20px -8px rgba(11, 42, 28, 0.6)' }}
          >
            {isAllIn
              ? 'Sett all-in'
              : betAmount != null
              ? `Sett bet: ${formatChips(betAmount)}`
              : 'Sett bet'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl font-semibold text-sm bg-cream-card text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Avbryt
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

export default PlaceBetSheet
